/**
 * The back-to-top launch.
 *
 * Dots fly in and assemble into a rocket standing on the button that was
 * pressed; it shakes, lights, and climbs out of the top of the screen on a
 * column of smoke while the page scrolls up underneath it.
 *
 * Self-contained on purpose: it paints into its own full-screen 2D canvas,
 * created on demand and removed when the smoke clears. That keeps it working
 * on every page — the WebGL particle field only exists on the home page, and
 * sits behind the footer where it could not be seen anyway.
 */

import { prefersReducedMotion } from './motion';

/** Phase lengths in ms: assemble, ignite, climb, then let the smoke clear. */
const FORM = 900;
const IGNITE = 500;
const LIFT = 1500;
const LINGER = 900;

const DOTS = 1600;
/** The rocket is drawn in a box this size, then scaled to the viewport. */
const BOX_W = 120;
const BOX_H = 164;

/* Same violets as the home page's particle field, per mode. */
const PALETTE = {
  dark: ['#7B45F5', '#A585FF', '#ECEAF2'],
  light: ['#5E19EC', '#9B6BFF', '#2A2540'],
};

type Dot = { tx: number; ty: number; sx: number; sy: number; delay: number; r: number; color: string; phase: number };
type Puff = { x: number; y: number; vx: number; vy: number; age: number; life: number; size: number };
type Spark = { x: number; y: number; vx: number; vy: number; age: number; life: number };

const easeOut = (x: number) => 1 - Math.pow(1 - x, 3);
const easeInOut = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/** Rasterise a rocket silhouette and return its filled pixels. */
function rocketPixels(): [number, number][] {
  const cv = document.createElement('canvas');
  cv.width = BOX_W;
  cv.height = BOX_H;
  const cx = cv.getContext('2d');
  if (!cx) return [[BOX_W / 2, BOX_H / 2]];
  cx.fillStyle = '#fff';
  cx.strokeStyle = '#fff';
  // Body: a pointed nose widening into the fuselage.
  cx.beginPath();
  cx.moveTo(60, 0);
  cx.bezierCurveTo(95, 40, 92, 110, 84, 150);
  cx.lineTo(36, 150);
  cx.bezierCurveTo(28, 110, 25, 40, 60, 0);
  cx.fill();
  // Fins.
  cx.beginPath();
  cx.moveTo(34, 104);
  cx.lineTo(6, 164);
  cx.lineTo(40, 150);
  cx.closePath();
  cx.moveTo(86, 104);
  cx.lineTo(114, 164);
  cx.lineTo(80, 150);
  cx.closePath();
  cx.fill();
  // Nozzle.
  cx.fillRect(46, 150, 28, 12);
  // Porthole: punch it out, then put the rim back.
  cx.globalCompositeOperation = 'destination-out';
  cx.beginPath();
  cx.arc(60, 62, 14, 0, Math.PI * 2);
  cx.fill();
  cx.globalCompositeOperation = 'source-over';
  cx.lineWidth = 3;
  cx.beginPath();
  cx.arc(60, 62, 13, 0, Math.PI * 2);
  cx.stroke();

  const d = cx.getImageData(0, 0, BOX_W, BOX_H).data;
  const px: [number, number][] = [];
  for (let y = 0; y < BOX_H; y += 2)
    for (let x = 0; x < BOX_W; x += 2) if (d[(y * BOX_W + x) * 4 + 3] > 128) px.push([x, y]);
  return px.length ? px : [[BOX_W / 2, BOX_H / 2]];
}

/** A soft round sprite; stamping it is far cheaper than a gradient per puff. */
function smokeSprite(rgb: string) {
  const s = document.createElement('canvas');
  s.width = s.height = 64;
  const cx = s.getContext('2d');
  if (cx) {
    const g = cx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, `rgba(${rgb},1)`);
    g.addColorStop(0.5, `rgba(${rgb},0.55)`);
    g.addColorStop(1, `rgba(${rgb},0)`);
    cx.fillStyle = g;
    cx.fillRect(0, 0, 64, 64);
  }
  return s;
}

/**
 * Play the launch from `origin` and scroll the page to the top.
 * Resolves when the animation has finished and cleaned up after itself.
 */
export function launchRocket(origin: HTMLElement): Promise<void> {
  if (prefersReducedMotion()) {
    window.scrollTo(0, 0);
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const W = window.innerWidth;
    const H = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio, 2);
    const canvas = document.createElement('canvas');
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;z-index:60;pointer-events:none';
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      window.scrollTo(0, 0);
      resolve();
      return;
    }
    document.body.appendChild(canvas);
    ctx.scale(dpr, dpr);

    const light = !document.documentElement.classList.contains('dark');
    const palette = light ? PALETTE.light : PALETTE.dark;
    // White smoke would vanish on the light theme's near-white ground.
    const smoke = smokeSprite(light ? '176,168,198' : '255,255,255');

    // Stand the rocket on the button, kept clear of the viewport edges.
    const rect = origin.getBoundingClientRect();
    const rocketH = Math.max(120, Math.min(190, H * 0.24));
    const scale = rocketH / BOX_H;
    const rocketW = BOX_W * scale;
    const cx = Math.max(rocketW / 2 + 12, Math.min(W - rocketW / 2 - 12, rect.left + rect.width / 2));
    const baseY = Math.max(rocketH + 24, Math.min(H - 8, rect.top - 14));
    const left = cx - rocketW / 2;
    const top = baseY - rocketH;

    const pixels = rocketPixels();
    const dots: Dot[] = Array.from({ length: DOTS }, () => {
      const p = pixels[(Math.random() * pixels.length) | 0];
      const angle = Math.random() * Math.PI * 2;
      const dist = 160 + Math.random() * Math.max(W, H) * 0.5;
      const c = Math.random();
      return {
        tx: left + (p[0] + Math.random() * 2) * scale,
        ty: top + (p[1] + Math.random() * 2) * scale,
        sx: cx + Math.cos(angle) * dist,
        sy: baseY - rocketH / 2 + Math.sin(angle) * dist,
        delay: Math.random() * 0.4,
        r: Math.random() < 0.05 ? 2.2 : 0.8 + Math.random() * 0.9,
        color: c < 0.45 ? palette[0] : c < 0.85 ? palette[1] : palette[2],
        phase: Math.random() * 6.28,
      };
    });
    const puffs: Puff[] = [];
    const sparks: Spark[] = [];

    const startScroll = window.scrollY;
    let scrolling = true;
    // The visitor taking over the scroll wins; the rocket still flies.
    const release = () => {
      scrolling = false;
    };
    window.addEventListener('wheel', release, { passive: true });
    window.addEventListener('touchstart', release, { passive: true });

    const t0 = performance.now();
    let prev = t0;
    const liftAt = FORM + IGNITE;
    const end = liftAt + LIFT + LINGER;

    const frame = (now: number) => {
      const t = now - t0;
      const dt = Math.min(3, (now - prev) / 16.67);
      prev = now;

      const lift = clamp01((t - liftAt) / LIFT);
      // Slow off the pad, then gone: the exponent is the acceleration curve.
      const climb = -(baseY + 120) * Math.pow(lift, 2.4);
      const rumble = t < FORM ? 0 : t < liftAt ? ((t - FORM) / IGNITE) * 2.2 : 2.2 * (1 - lift);
      const shakeX = (Math.random() - 0.5) * 2 * rumble;
      const shakeY = (Math.random() - 0.5) * rumble;
      const nozzleX = cx + shakeX;
      const nozzleY = baseY + climb;

      if (scrolling && t >= liftAt) {
        window.scrollTo({ top: startScroll * (1 - easeInOut(lift)), behavior: 'instant' });
      }

      // Exhaust: a trickle while the engine lights, then full thrust.
      if (t > FORM + IGNITE * 0.25 && nozzleY > -60 && lift < 1) {
        const thrust = t < liftAt ? 3 : 7;
        for (let i = 0; i < thrust * dt; i++) {
          puffs.push({
            x: nozzleX + (Math.random() - 0.5) * 14 * scale,
            y: nozzleY + Math.random() * 6,
            vx: (Math.random() - 0.5) * 2.6,
            vy: 3 + Math.random() * 4.5,
            age: 0,
            life: 900 + Math.random() * 900,
            size: (10 + Math.random() * 10) * scale,
          });
        }
        for (let i = 0; i < 4 * dt; i++) {
          sparks.push({
            x: nozzleX + (Math.random() - 0.5) * 12 * scale,
            y: nozzleY,
            vx: (Math.random() - 0.5) * 1.4,
            vy: 6 + Math.random() * 6,
            age: 0,
            life: 180 + Math.random() * 160,
          });
        }
      }

      ctx.clearRect(0, 0, W, H);

      // Smoke first, so the rocket stands in front of its own exhaust.
      ctx.globalCompositeOperation = 'source-over';
      const ground = baseY + 10;
      for (let i = puffs.length - 1; i >= 0; i--) {
        const p = puffs[i];
        p.age += dt * 16.67;
        if (p.age >= p.life) {
          puffs.splice(i, 1);
          continue;
        }
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= Math.pow(0.985, dt);
        p.vy *= Math.pow(0.955, dt);
        // Exhaust hitting the pad has nowhere to go but sideways — but only
        // while there is a pad: once the rocket is climbing the page is
        // scrolling away beneath it, and the smoke just hangs and thins.
        if (lift === 0 && p.y > ground) {
          p.y = ground;
          p.vx = Math.max(-3.5, Math.min(3.5, p.vx + Math.sign(p.vx || Math.random() - 0.5) * 0.35 * dt));
        }
        const k = p.age / p.life;
        const size = p.size + k * 70 * scale;
        ctx.globalAlpha = 0.42 * Math.pow(1 - k, 1.6);
        ctx.drawImage(smoke, p.x - size, p.y - size, size * 2, size * 2);
      }

      ctx.globalCompositeOperation = light ? 'source-over' : 'lighter';
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.age += dt * 16.67;
        if (s.age >= s.life) {
          sparks.splice(i, 1);
          continue;
        }
        s.x += s.vx * dt;
        s.y += s.vy * dt;
        ctx.globalAlpha = 1 - s.age / s.life;
        ctx.fillStyle = light ? palette[0] : '#FFFFFF';
        ctx.beginPath();
        ctx.arc(s.x, s.y, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }

      if (nozzleY > -rocketH) {
        const form = t / FORM;
        for (const d of dots) {
          const e = easeOut(clamp01((form - d.delay) / (1 - 0.4)));
          if (e <= 0) continue;
          const wobble = Math.sin(t * 0.004 + d.phase) * 0.6;
          ctx.globalAlpha = Math.min(1, e * 1.4);
          ctx.fillStyle = d.color;
          ctx.beginPath();
          ctx.arc(
            d.sx + (d.tx - d.sx) * e + shakeX + wobble,
            d.sy + (d.ty - d.sy) * e + climb * e + shakeY,
            d.r,
            0,
            Math.PI * 2,
          );
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;

      if (t < end) {
        requestAnimationFrame(frame);
        return;
      }
      window.removeEventListener('wheel', release);
      window.removeEventListener('touchstart', release);
      canvas.remove();
      resolve();
    };
    requestAnimationFrame(frame);
  });
}
